import { ads } from '@paramount-streaming/blackbird';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';
import { TPluginConfigFactory } from 'tag/ts/tag/player/adapters/avia/types/TPluginConfigFactory';

/**
 * @copyright © Paramount Streaming
 * @description Wraps ads plugin from pluto-tv to be integrated with SmartTag
 */
export class AdsPlugin extends AviaPlugin implements IAviaPlugin {
	static readonly #DEFAULT_INACTIVITY_TIMEOUT_MS = 5000;
	package = ads as unknown as TPluginConfigFactory;
	namespaceKey = 'plugin_ads';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AdsPlugin'
			}
		);
	}

	/**
	 * Should be used if params.STITCHER is non-empty
	 * @returns {boolean}
	 */
	shouldBeUsed() {
		return this.hasStitcherParams();
	}

	/**
	 * Safe to reinitialize mid-session (PLBCK-8461): reads `tag.params.STITCHER` fresh on
	 * every construction, and Blackbird's ad-manager/beacon-manager teardown is self-contained
	 * per plugin instance — no shared state that a reinit would corrupt.
	 * @returns {boolean}
	 */
	isReinitializable() {
		return true;
	}

	/**
	 * Returns parameters required to initialize ads from pluto-tv
	 * @see {@link https://github.com/paramount-streaming/playback-javascript-lib/blob/main/libs/blackbird/src/player/adapters/avia/plugins/ads/README.md}
	 * @example [Initializing ads in Pluto tv demo] https://github.com/paramount-streaming/playback-javascript-lib/blob/main/app/src/smart-tag/useSmartTagVideoPlayer.ts#L94
	 */
	getFactoryParams() {
		return {
			...super.getFactoryParams(),
			// set necessary values from STITCHER params
			adTechnology: this.tag.params?.STITCHER?.adTechnology,
			adBreakSettings: {
				preroll: this.tag.params?.STITCHER?.adBreakPreroll,
				postroll: this.tag.params?.STITCHER?.adBreakPostroll
			},
			timedMetadataInactivityTimeoutMs: this.tag.params?.STITCHER?.inactivityTimeout ?? AdsPlugin.#DEFAULT_INACTIVITY_TIMEOUT_MS,
			method: this.tag.params?.STITCHER?.method,
			type: this.tag.params?.STITCHER?.streamType?.toLowerCase(),
			nerdMode: undefined // NOSONAR: undefined (not null) is intentional — merge utilities and JSON.stringify strip undefined keys, preserving Blackbird defaults
		};
	}
}